import {
  collection,
  firestore,
  onSnapshot,
  query,
  where,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannelMessageBookmark } from '@ps/models/lib/community-channel';

/**
 * Subscribe to the current user's bookmarks in a single channel.
 * Bookmarks live under
 * `privateProfiles/{userId}/communityChannelBookmarks` and we filter
 * by `channelId` so each open channel only listens to its own
 * relevant rows.
 *
 * Emits the set of bookmarked `messageId`s so each bubble can do an
 * O(1) lookup for the bookmark highlight / kebab "Un-bookmark"
 * label state.
 */
export function subscribeToMyMessageBookmarks(
  userId: string,
  channelId: string,
  onChange: (messageIds: Set<string>) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = collection(
    db,
    'privateProfiles',
    userId,
    'communityChannelBookmarks',
  );
  const q = query(ref, where('channelId', '==', channelId));

  const unsubscribe = onSnapshot(
    q,
    snapshot => {
      const ids = new Set<string>();
      snapshot.forEach(doc => {
        const data = snapshotToDataWithId<CommunityChannelMessageBookmark>(doc);
        if (data?.messageId) ids.add(data.messageId);
      });
      onChange(ids);
    },
    error => onError?.(error),
  );
  return unsubscribe;
}
